import React, { useState, useEffect } from 'react';
import { Product, ProductBatch } from '../types';
import { Search, Plus, Pencil, Trash2, X, AlertCircle, Layers, Box, RefreshCw, Eye, Calendar, Hash } from 'lucide-react';

interface InventoryProps {
  products: Product[];
  onAddProduct: (product: Omit<Product, 'id' | 'batches'>) => void;
  onEditProduct: (product: Product) => void;
  onDeleteProduct: (id: string) => void;
}

const Inventory: React.FC<InventoryProps> = ({ products, onAddProduct, onEditProduct, onDeleteProduct }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [viewingBatchProduct, setViewingBatchProduct] = useState<Product | null>(null);
  
  const [formData, setFormData] = useState<Omit<Product, 'id' | 'batches'>>({
    name: '',
    category: 'Tablets',
    stripsPerPack: 1,
    unitsPerStrip: 1,
    pricePack: 0,
    priceStrip: 0,
    priceUnit: 0,
    cost: 0,
    stock: 0,
    expiryDate: '',
    batchNumber: '',
    vendorId: '',
    autoUpdatePricing: true
  });

  const isTablet = formData.category === 'Tablets';

  // Synchronize derived pricing fields whenever core values change (only for Tablets)
  useEffect(() => {
    if (!isTablet) {
      // For non-tablets, ensure everything is treated as 1 unit
      if (formData.stripsPerPack !== 1 || formData.unitsPerStrip !== 1) {
        setFormData(prev => ({
          ...prev,
          stripsPerPack: 1,
          unitsPerStrip: 1,
          pricePack: prev.priceUnit,
          priceStrip: prev.priceUnit
        }));
      }
      return;
    }

    if (formData.autoUpdatePricing) {
      const stripsPerPack = Math.max(1, formData.stripsPerPack);
      const unitsPerStrip = Math.max(1, formData.unitsPerStrip);
      
      const calculatedPriceStrip = parseFloat((formData.pricePack / stripsPerPack).toFixed(2));
      const calculatedPriceUnit = parseFloat((calculatedPriceStrip / unitsPerStrip).toFixed(2));
      
      if (formData.priceStrip !== calculatedPriceStrip || formData.priceUnit !== calculatedPriceUnit) {
        setFormData(prev => ({
          ...prev,
          priceStrip: calculatedPriceStrip,
          priceUnit: calculatedPriceUnit
        }));
      }
    }
  }, [formData.pricePack, formData.stripsPerPack, formData.unitsPerStrip, formData.autoUpdatePricing, formData.category, isTablet]);

  const filteredProducts = products.filter(p => 
    p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (p.batchNumber && p.batchNumber.toLowerCase().includes(searchTerm.toLowerCase())) ||
    p.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleOpenAdd = () => {
    setEditingId(null);
    setFormData({ 
      name: '', 
      category: 'Tablets', 
      stripsPerPack: 10, 
      unitsPerStrip: 10,
      pricePack: 0, 
      priceStrip: 0, 
      priceUnit: 0,
      cost: 0, 
      stock: 0, 
      expiryDate: '', 
      batchNumber: '', 
      vendorId: '',
      autoUpdatePricing: true
    });
    setIsModalOpen(true);
  };

  const handleOpenEdit = (product: Product) => {
    setEditingId(product.id);
    setFormData({
      name: product.name,
      category: product.category,
      stripsPerPack: product.stripsPerPack,
      unitsPerStrip: product.unitsPerStrip,
      pricePack: product.pricePack,
      priceStrip: product.priceStrip,
      priceUnit: product.priceUnit,
      cost: product.cost,
      stock: product.stock,
      expiryDate: product.expiryDate,
      batchNumber: product.batchNumber,
      vendorId: product.vendorId,
      autoUpdatePricing: product.autoUpdatePricing ?? true
    });
    setIsModalOpen(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingId) {
      onEditProduct({ ...formData, id: editingId, batches: products.find(p => p.id === editingId)?.batches || [] });
    } else {
      onAddProduct(formData);
    }
    setIsModalOpen(false);
  };

  const formatStock = (totalUnits: number, p: Product | { category: string; stripsPerPack: number; unitsPerStrip: number }) => {
    if (p.category !== 'Tablets') return `${totalUnits} Un`;

    const unitsPerPack = (p.stripsPerPack || 1) * (p.unitsPerStrip || 1);
    const packs = Math.floor(totalUnits / unitsPerPack);
    const remainingAfterPacks = totalUnits % unitsPerPack;
    const strips = Math.floor(remainingAfterPacks / (p.unitsPerStrip || 1));
    const units = remainingAfterPacks % (p.unitsPerStrip || 1);
    
    let parts = [];
    if (packs > 0) parts.push(`${packs} Pk`);
    if (strips > 0) parts.push(`${strips} St`);
    if (units > 0) parts.push(`${units} Un`);
    
    return parts.length > 0 ? parts.join(', ') : '0 Un';
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="p-6 border-b border-slate-200 flex flex-col md:flex-row justify-between items-center gap-4">
          <h2 className="text-lg font-bold text-slate-800">Product Inventory</h2>
          <div className="flex w-full md:w-auto gap-3">
            <div className="relative flex-1 md:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
              <input 
                type="text" 
                placeholder="Search products..." 
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm"
              />
            </div>
            <button 
              onClick={handleOpenAdd}
              className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
            >
              <Plus size={18} />
              Add Product
            </button>
          </div>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
                <th className="p-4 font-medium border-b border-slate-100">Product Name</th>
                <th className="p-4 font-medium border-b border-slate-100">Packaging</th>
                <th className="p-4 font-medium border-b border-slate-100">Batch / Expiry</th>
                <th className="p-4 font-medium border-b border-slate-100">Stock (Units)</th>
                <th className="p-4 font-medium border-b border-slate-100">Base Cost</th>
                <th className="p-4 font-medium border-b border-slate-100">Unit Price</th>
                <th className="p-4 font-medium border-b border-slate-100 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredProducts.map(product => (
                <tr key={product.id} className="hover:bg-slate-50 transition-colors group">
                  <td className="p-4">
                    <div className="font-medium text-slate-900">{product.name}</div>
                    <div className="text-xs text-slate-400">{product.category}</div>
                  </td>
                  <td className="p-4 text-xs text-slate-600">
                    {product.category === 'Tablets' ? (
                      <div className="flex flex-col gap-0.5">
                        <span className="flex items-center gap-1"><Box size={10}/> {product.stripsPerPack} Strips/Pk</span>
                        <span className="flex items-center gap-1"><Layers size={10}/> {product.unitsPerStrip} Units/St</span>
                      </div>
                    ) : (
                      <span className="text-slate-400 font-medium italic">Single Unit Only</span>
                    )}
                  </td>
                  <td className="p-4 text-sm">
                    <div className="space-y-2">
                        <div className="flex flex-col gap-0.5">
                            <div className="font-mono text-xs text-slate-600">{product.batchNumber}</div>
                            <span className={`text-[10px] font-bold uppercase ${
                            new Date(product.expiryDate) < new Date() ? 'text-red-600' : 'text-emerald-600'
                            }`}>
                            {product.expiryDate}
                            </span>
                        </div>
                        <button 
                            onClick={() => setViewingBatchProduct(product)}
                            className="flex items-center gap-1 text-[10px] font-bold text-slate-400 hover:text-emerald-600 uppercase tracking-tighter bg-slate-100 hover:bg-emerald-50 px-2 py-1 rounded transition-all"
                        >
                            <Eye size={10} /> View Details
                        </button>
                    </div>
                  </td>
                  <td className="p-4 text-sm">
                    <div className="flex flex-col gap-1">
                      <span className={`font-bold ${product.stock < 100 ? 'text-red-600' : 'text-slate-700'}`}>
                        {product.stock} Units
                      </span>
                      <span className="text-[10px] text-slate-400 font-medium">
                        ({formatStock(product.stock, product)})
                      </span>
                    </div>
                  </td>
                  <td className="p-4 text-sm font-medium text-red-600">RS. {product.cost.toFixed(2)}</td>
                  <td className="p-4 text-sm font-bold text-emerald-600">RS. {product.priceUnit.toFixed(2)}</td>
                  <td className="p-4 text-right">
                    <div className="flex justify-end gap-2">
                      <button 
                        onClick={() => handleOpenEdit(product)}
                        className="p-2 bg-blue-50 border border-blue-200 text-blue-600 hover:bg-blue-100 hover:text-blue-700 rounded-lg transition-colors shadow-sm"
                        title="Edit Product"
                      >
                        <Pencil size={16} />
                      </button>
                      <button 
                        onClick={() => onDeleteProduct(product.id)}
                        className="p-2 bg-red-50 border border-red-200 text-red-600 hover:bg-red-100 hover:text-red-700 rounded-lg transition-colors shadow-sm"
                        title="Delete Product"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          
          {filteredProducts.length === 0 && (
             <div className="p-12 text-center text-slate-500 flex flex-col items-center">
               <AlertCircle size={48} className="text-slate-200 mb-3" />
               <p>No products found matching your search.</p>
             </div>
          )}
        </div>
      </div>

      {/* Batch Details Modal */}
      {viewingBatchProduct && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-2xl rounded-2xl shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-200 flex flex-col max-h-[85vh]">
            <div className="p-5 border-b border-slate-100 flex justify-between items-center bg-slate-50">
               <div>
                  <h3 className="font-bold text-lg text-slate-800">Batch Inventory</h3>
                  <p className="text-xs text-emerald-600 font-medium">{viewingBatchProduct.name}</p>
               </div>
               <button onClick={() => setViewingBatchProduct(null)} className="text-slate-400 hover:text-slate-600 p-2 hover:bg-slate-200 rounded-full transition-all">
                 <X size={20} />
               </button>
            </div>
            
            <div className="flex-1 overflow-y-auto p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    <div className="bg-slate-50 p-4 rounded-xl border border-slate-100">
                        <p className="text-[10px] font-bold text-slate-400 uppercase mb-1">Total Available Stock</p>
                        <p className="text-xl font-bold text-slate-900">{viewingBatchProduct.stock} Units</p>
                        <p className="text-xs text-slate-500 italic mt-0.5">({formatStock(viewingBatchProduct.stock, viewingBatchProduct)})</p>
                    </div>
                    <div className="bg-slate-50 p-4 rounded-xl border border-slate-100">
                        <p className="text-[10px] font-bold text-slate-400 uppercase mb-1">Product Category</p>
                        <p className="text-xl font-bold text-slate-900">{viewingBatchProduct.category}</p>
                    </div>
                </div>

                <div className="border border-slate-100 rounded-xl overflow-hidden">
                    <table className="w-full text-left text-sm">
                        <thead className="bg-slate-50 text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                            <tr>
                                <th className="p-4 border-b">Batch #</th>
                                <th className="p-4 border-b">Expiry Date</th>
                                <th className="p-4 border-b">Quantity Left</th>
                                <th className="p-4 border-b text-right">Unit Cost</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {viewingBatchProduct.batches?.length > 0 ? (
                                viewingBatchProduct.batches.map((batch) => (
                                    <tr key={batch.id} className="hover:bg-slate-50 transition-colors">
                                        <td className="p-4">
                                            <div className="flex items-center gap-2">
                                                <Hash size={14} className="text-slate-300" />
                                                <span className="font-mono text-xs font-bold text-slate-700">{batch.batchNumber}</span>
                                            </div>
                                        </td>
                                        <td className="p-4">
                                            <div className="flex items-center gap-2">
                                                <Calendar size={14} className="text-slate-300" />
                                                <span className={`text-xs font-medium ${new Date(batch.expiryDate) < new Date() ? 'text-red-500' : 'text-slate-700'}`}>
                                                    {batch.expiryDate}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="p-4">
                                            <div className="flex flex-col">
                                                <span className="font-bold text-slate-800">{batch.quantity} Units</span>
                                                <span className="text-[10px] text-slate-400">({formatStock(batch.quantity, viewingBatchProduct)})</span>
                                            </div>
                                        </td>
                                        <td className="p-4 text-right font-medium text-red-600">
                                            RS. {batch.cost.toFixed(2)}
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={4} className="p-8 text-center text-slate-400 italic">No detailed batch data available for this product.</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
            
            <div className="p-4 border-t border-slate-100 bg-slate-50 flex justify-end">
                <button onClick={() => setViewingBatchProduct(null)} className="px-8 py-2 bg-slate-900 text-white rounded-lg font-bold text-sm shadow-lg hover:bg-slate-800 transition-all">
                    Close Details
                </button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Product Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-2xl rounded-xl shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-200 max-h-[90vh] flex flex-col">
            <div className="p-4 border-b border-slate-100 flex justify-between items-center bg-slate-50">
               <h3 className="font-bold text-lg text-slate-800">
                 {editingId ? 'Edit Product Details' : 'Add New Product'}
               </h3>
               <button onClick={() => setIsModalOpen(false)} className="text-slate-400 hover:text-slate-600">
                 <X size={20} />
               </button>
            </div>
            
            <div className="overflow-y-auto p-6">
                <form id="productForm" onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="col-span-full">
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-1">Product Name</label>
                    <input required type="text" placeholder="e.g. Panadol Extra" className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 font-medium" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} />
                    </div>
                    
                    <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-1">Category</label>
                    <select required className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 bg-white font-medium" value={formData.category} onChange={e => setFormData({...formData, category: e.target.value})} >
                        <option value="Tablets">Tablets</option>
                        <option value="Syrups">Syrups</option>
                        <option value="Antibiotics">Antibiotics</option>
                        <option value="Injections">Injections</option>
                        <option value="Supplements">Supplements</option>
                    </select>
                    </div>

                    <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-1">Batch Number</label>
                    <input required type="text" className="w-full px-3 py-2 border border-slate-300 rounded-lg font-mono text-sm" value={formData.batchNumber} onChange={e => setFormData({...formData, batchNumber: e.target.value})} />
                    </div>

                    {isTablet && (
                      <>
                        <div>
                        <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-1">Strips per Pack</label>
                        <input required type="number" min="1" className="w-full px-3 py-2 border border-slate-300 rounded-lg" value={formData.stripsPerPack} onChange={e => setFormData({...formData, stripsPerPack: parseInt(e.target.value) || 1})} />
                        </div>

                        <div>
                        <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-1">Units per Strip</label>
                        <input required type="number" min="1" className="w-full px-3 py-2 border border-slate-300 rounded-lg" value={formData.unitsPerStrip} onChange={e => setFormData({...formData, unitsPerStrip: parseInt(e.target.value) || 1})} />
                        </div>
                      </>
                    )}

                    <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-1">Expiry Date</label>
                    <input required type="date" className="w-full px-3 py-2 border border-slate-300 rounded-lg" value={formData.expiryDate} onChange={e => setFormData({...formData, expiryDate: e.target.value})} />
                    </div>

                    <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-1">Initial Stock (Units)</label>
                    <input required type="number" min="0" className="w-full px-3 py-2 border border-slate-300 rounded-lg font-bold text-slate-700" value={formData.stock} onChange={e => setFormData({...formData, stock: parseInt(e.target.value) || 0})} />
                    </div>

                    <div className="col-span-full border-t border-slate-100 pt-4">
                      <div className="flex justify-between items-center mb-4">
                        <h4 className="text-[11px] font-black uppercase text-slate-400 tracking-widest">Selling Prices</h4>
                        {isTablet && (
                          <div className="flex items-center gap-2">
                            <input 
                              type="checkbox" 
                              id="autoUpdatePricing"
                              checked={formData.autoUpdatePricing}
                              onChange={(e) => setFormData({ ...formData, autoUpdatePricing: e.target.checked })}
                              className="w-4 h-4 text-emerald-600 rounded focus:ring-emerald-500 cursor-pointer"
                            />
                            <label htmlFor="autoUpdatePricing" className="text-[10px] font-bold text-slate-500 flex items-center gap-1 uppercase tracking-tight cursor-pointer">
                              <RefreshCw size={12} className={formData.autoUpdatePricing ? 'animate-spin-slow' : ''} />
                              Sync strips/units
                            </label>
                          </div>
                        )}
                      </div>
                      <div className={`grid grid-cols-1 ${isTablet ? 'md:grid-cols-3' : ''} gap-4`}>
                        {isTablet && (
                          <>
                            <div>
                                <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Price per Pack</label>
                                <div className="relative">
                                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-bold">RS.</span>
                                  <input required type="number" step="0.01" className="w-full pl-10 pr-3 py-2 border border-emerald-500/30 bg-emerald-50/10 rounded-lg font-bold text-emerald-600 focus:ring-2 focus:ring-emerald-500 outline-none" value={formData.pricePack} onChange={e => setFormData({...formData, pricePack: parseFloat(e.target.value) || 0})} />
                                </div>
                            </div>
                            <div>
                                <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Price per Strip</label>
                                <div className="relative">
                                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-bold">RS.</span>
                                  <input required type="number" step="0.01" className={`w-full pl-10 pr-3 py-2 border border-slate-300 rounded-lg font-bold text-emerald-600 focus:ring-2 focus:ring-emerald-500 outline-none ${formData.autoUpdatePricing ? 'bg-slate-50 cursor-not-allowed opacity-70' : ''}`} value={formData.priceStrip} onChange={e => setFormData({...formData, priceStrip: parseFloat(e.target.value) || 0})} disabled={formData.autoUpdatePricing} />
                                </div>
                            </div>
                          </>
                        )}
                        <div>
                            <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">{isTablet ? 'Price per Unit' : 'Selling Price (per Unit)'}</label>
                            <div className="relative">
                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-bold">RS.</span>
                                <input required type="number" step="0.01" className={`w-full pl-10 pr-3 py-2 border border-slate-300 rounded-lg font-bold text-emerald-600 focus:ring-2 focus:ring-emerald-500 outline-none ${isTablet && formData.autoUpdatePricing ? 'bg-slate-50 cursor-not-allowed opacity-70' : ''}`} value={formData.priceUnit} onChange={e => setFormData({...formData, priceUnit: parseFloat(e.target.value) || 0})} disabled={isTablet && formData.autoUpdatePricing} />
                            </div>
                        </div>
                      </div>
                    </div>

                    <div className="col-span-full">
                      <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-1">Cost per Unit (RS.)</label>
                      <input required type="number" step="0.01" className="w-full px-3 py-2 border border-red-300 rounded-lg font-medium text-red-600" value={formData.cost} onChange={e => setFormData({...formData, cost: parseFloat(e.target.value) || 0})} />
                    </div>
                </div>
                </form>
            </div>

            <div className="p-4 border-t border-slate-100 bg-slate-50 flex gap-3">
                 <button type="button" onClick={() => setIsModalOpen(false)} className="flex-1 px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 font-bold transition-all">Cancel</button>
                 <button type="submit" form="productForm" className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 font-bold transition-all shadow-md active:scale-[0.98]">{editingId ? 'Save Changes' : 'Register Product'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Inventory;